import { Component, OnInit } from '@angular/core';
import { Chart, ChartDataset, ChartOptions } from 'chart.js';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { TrafficService } from '_services/traffic.service';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { BaseChartDirective } from 'ng2-charts';
import { AsyncPipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';


@Component({
  selector: 'app-traffic-chart',
  templateUrl: './traffic-chart.component.html',
  styleUrls: ['./traffic-chart.component.css'],
  imports: [BaseChartDirective, AsyncPipe, MatCardModule]
})
export class TrafficChartComponent implements OnInit {
  public resultsDataset$: Observable<ChartDataset[]>;
  public completionDataset$: Observable<ChartDataset[]>;
  public resultsChartOptions: ChartOptions;
  public completionChartOptions: ChartOptions;

  public lineData = new BehaviorSubject<ChartDataset[]>([]);
  constructor(public ts: TrafficService) {
    Chart.register(ChartDataLabels);
    const charts = this.buildChartOptions();
    this.resultsChartOptions = charts.results;
    this.completionChartOptions = charts.completion;

    this.resultsDataset$ = this.ts.trafficLineData.pipe(
      map((datasets) => {
        const resultsDataset = datasets.find(dataset => dataset.label === 'Results');
        if (!resultsDataset) {
          return [];
        }
        const datasetWithoutYAxisID = { ...resultsDataset, type: 'bar' as const } as ChartDataset;
        delete (datasetWithoutYAxisID as ChartDataset & { yAxisID?: string; }).yAxisID;
        return [datasetWithoutYAxisID];
      })
    );

    this.completionDataset$ = combineLatest([this.ts.trafficLineData, this.ts.isAllPeriods]).pipe(
      map(([datasets, isAllPeriods]) => {
        const completionDataset = datasets.find(dataset => dataset.label === 'Completion %');
        if (!completionDataset) {
          return [];
        }
        const datasetWithoutYAxisID = { ...completionDataset };
        delete (datasetWithoutYAxisID as ChartDataset & { yAxisID?: string; }).yAxisID;
        if (datasetWithoutYAxisID.datalabels) {
          datasetWithoutYAxisID.datalabels = { ...datasetWithoutYAxisID.datalabels, display: !isAllPeriods };
        } else {
          datasetWithoutYAxisID.datalabels = { display: !isAllPeriods };
        }
        return [datasetWithoutYAxisID];
      })
    );
  }

  ngOnInit() {
    this.ts.isAllPeriods.subscribe(isAllPeriods => {
      if (this.resultsChartOptions?.plugins?.datalabels) {
        this.resultsChartOptions.plugins.datalabels.display = !isAllPeriods;
      }
      if (this.completionChartOptions?.plugins?.datalabels) {
        this.completionChartOptions.plugins.datalabels.display = !isAllPeriods;
      }
    });

    if (this.resultsChartOptions?.plugins?.datalabels) {
      this.resultsChartOptions.plugins.datalabels.display = !this.ts.isAllPeriods.value;
    }
    if (this.completionChartOptions?.plugins?.datalabels) {
      this.completionChartOptions.plugins.datalabels.display = !this.ts.isAllPeriods.value;
    }
  }

  private buildChartOptions(): { results: ChartOptions; completion: ChartOptions } {
    this.lineData.next([]);
    return {
      results: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            right: 20
          },
        },
        scales: {
          y: {
            type: 'linear',
            position: 'left',
            afterFit: (axis) => {
              axis.width = 50;
            },
          }
        },
        plugins: {
          legend: {
            display: false
          },
          title: {
            display: true,
            text: 'Results'
          },
          datalabels: {
            display: true,
            backgroundColor: 'rgba(255,255,255,1)',
            borderColor: 'rgba(0,0,0,.2)',
            borderRadius: 6,
            borderWidth: 1,
            color: 'black',
            font: {
              size: 10
            },
            formatter: (value: number) => {
              return Math.round(value).toLocaleString();
            }
          }
        }
      },
      completion: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            right: 20
          },
        },
        scales: {
          x: {
            display: true
          },
          y: {
            type: 'linear',
            position: 'left',
            afterFit: (axis) => {
              axis.width = 50;
            },
          }
        },
        plugins: {
          legend: {
            display: false
          },
          title: {
            display: true,
            text: 'Completion %'
          },
          datalabels: {
            display: true,
            backgroundColor: 'rgba(255,255,255,1)',
            borderColor: 'rgba(0,0,0,.2)',
            borderRadius: 6,
            borderWidth: 1,
            color: 'black',
            font: {
              size: 10
            },
            formatter: (value: number) => {
              return Math.round(value) + '%';
            }
          }
        }
      }
    };
  }
}
